import type {
  CalendarWorkItemDto,
  CampaignCalendarControllerWorkIncludeItem,
} from 'src/apis/backend/generated/models';

// ----------------------------------------------------------------------

/** An inclusive window, in the brand's own dates. */
export interface CalendarRange {
  from: string;
  to: string;
}

/** A local date as `YYYY-MM-DD`, without the UTC shift `toISOString` applies. */
export function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

export function todayKey(): string {
  return toDateKey(new Date());
}

/** The window a board opens on, before it reports the one it actually drew. */
export function initialRange(): CalendarRange {
  const now = new Date();
  return {
    from: toDateKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    to: toDateKey(new Date(now.getFullYear(), now.getMonth() + 2, 0)),
  };
}

export type BallHolder = 'client' | 'nexmail' | 'system' | 'closed';

/**
 * Who holds the ball.
 *
 * An item with no ticket is read by whether it is waiting on the customer's
 * yes, because that is the only thing they can do about it. Once a ticket
 * exists the ticket's own ball holder takes over.
 */
export function ballOf(item: CalendarWorkItemDto): BallHolder {
  if (item.ticket) return item.ticket.ballHolder as BallHolder;
  if (item.approvalStatus === 'pending') return 'client';
  if (item.approvalStatus === 'skipped') return 'closed';
  return 'system';
}

/** Whether an item is drawn muted rather than in its own colour. */
export function isMuted(item: CalendarWorkItemDto): boolean {
  return item.approvalStatus === 'skipped' || item.status === 'dismissed';
}

/**
 * The work view's `include` list, as one comma-separated value.
 *
 * The generated type is an array, and axios serialises an array parameter as
 * `include[]=templates&include[]=rounds`. Fastify's default query parser does
 * not expand that bracket, so the server receives a parameter literally named
 * `include[]`, the validation pipe whitelists it away, and the request reads as
 * "no include at all" -- which silently falls back to design cards only: no
 * thin weeks, no round counts, no sign-off rollup. The endpoint documents the
 * comma-separated form as well, and that one survives serialisation intact, so
 * that is what goes on the wire.
 */
export function calendarInclude(
  ...values: CampaignCalendarControllerWorkIncludeItem[]
): CampaignCalendarControllerWorkIncludeItem[] {
  return values.join(',') as unknown as CampaignCalendarControllerWorkIncludeItem[];
}

/**
 * How the same window is drawn.
 *
 * A view mode, not a tab: every mode answers the same question about the same
 * rows, which is what a segmented control means and what a tab does not. It
 * lives in the URL so a list someone is reading survives a reload and can be
 * sent to somebody else.
 */
export type CalendarViewMode = 'plan' | 'list' | 'month';

export function toViewMode(value: unknown): CalendarViewMode | undefined {
  return value === 'plan' || value === 'list' || value === 'month' ? value : undefined;
}
